import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, ChevronLeft, ChevronRight, CalendarX2, AlertTriangle, Mail, ChevronDown, TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getCalendarEvents } from '../services/calendarApi'
import { getOutlookCalendarEvents } from '@/features/outlook-calendar/services/outlookCalendarApi'
import { getIntegrations } from '@/features/integrations/services/integrationsApi'
import { listBookings } from '@/features/bookings/services/bookingsApi'
import type { CalendarEvent } from '../types/index'

type ViewMode = 'day' | 'week' | 'agenda'

type CalendarProvider = 'google' | 'outlook'

type MergedEvent = CalendarEvent & { provider: CalendarProvider }

type ConnectedAccount = { provider: CalendarProvider; email: string }

// A degraded 'both' booking always has exactly one calendarRefs entry — the
// leg that succeeded. The other provider is the one that failed.
type DegradedInfo = { succeededProvider: CalendarProvider; failedProvider: CalendarProvider }

const PROVIDER_LABEL: Record<CalendarProvider, string> = { google: 'Google Calendar', outlook: 'Outlook Calendar' }
const PROVIDER_SHORT_LABEL: Record<CalendarProvider, string> = { google: 'Google', outlook: 'Outlook' }
const OTHER_PROVIDER: Record<CalendarProvider, CalendarProvider> = { google: 'outlook', outlook: 'google' }

// Literal brand colors (pen.dev design spec) — not theme tokens, since a
// provider's brand color must stay fixed regardless of light/dark mode.
// Matches the existing precedent of hardcoded status-dot colors (e.g.
// '#10B981') in features/integrations/components/IntegrationCard.tsx.
const PROVIDER_DOT_COLOR: Record<CalendarProvider, string> = { google: '#4285F4', outlook: '#0A66C2' }

function initialFor(event: MergedEvent): string {
  const source = event.attendees[0]?.name || event.attendees[0]?.email || event.summary || '?'
  return source.trim().charAt(0).toUpperCase() || '?'
}

function attendeeLine(event: MergedEvent): string | null {
  const first = event.attendees[0]
  if (!first) return null
  if (first.name && first.email && first.name !== first.email) return `${first.name} · ${first.email}`
  return first.name || first.email || null
}

// Read-only week/day/agenda view (ZAI-384). Booking/rescheduling is Epic 5 —
// this never writes anything. Merges events from every connected calendar
// provider (Google Calendar, Outlook Calendar) into one list — a rep can
// plausibly have a personal Google Calendar and a work Outlook Calendar both
// connected, and this is a read-only aggregation with no "which one is the
// real inbox" ambiguity the way a send action would have. Self-contained so
// it can be relocated once ZAI-383 decides where it lives in the app shell's
// navigation.
// refreshToken: bump it (e.g. after a booking) to force a refetch of the
// current range without the caller needing to know this component's internals.
// bookButton: the "Book meeting" trigger, owned by the caller (which also owns
// the modal's open state) — rendered here so it can sit inside the summary row.
type Props = { workspaceId: string; refreshToken?: number; bookButton?: React.ReactNode }

// Fetches one provider's events and classifies the outcome — 'not_found' for
// a provider the rep never connected is not an error (it just contributes no
// events), unlike 'needs_reconnect' or an unrecognized failure.
async function fetchProviderEvents(
  provider: CalendarProvider,
  fetchFn: (workspaceId: string, from: string, to: string) => Promise<CalendarEvent[]>,
  workspaceId: string,
  from: string,
  to: string,
): Promise<{ provider: CalendarProvider; events: MergedEvent[]; error: 'not_found' | 'needs_reconnect' | 'generic' | null }> {
  try {
    const events = await fetchFn(workspaceId, from, to)
    return { provider, events: events.map((e) => ({ ...e, provider })), error: null }
  } catch (err) {
    const message = err instanceof Error ? err.message : ''
    const error = message === 'needs_reconnect' || message === 'not_found' ? message : 'generic'
    return { provider, events: [], error }
  }
}

function startOfDay(d: Date): Date {
  const out = new Date(d)
  out.setHours(0, 0, 0, 0)
  return out
}

function startOfWeek(d: Date): Date {
  const out = startOfDay(d)
  const day = out.getDay() // 0 = Sunday
  out.setDate(out.getDate() - day)
  return out
}

function addDays(d: Date, days: number): Date {
  const out = new Date(d)
  out.setDate(out.getDate() + days)
  return out
}

function formatDayHeader(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function dayKey(iso: string): string {
  return iso.slice(0, 10)
}

// Day-box keys must read out the same calendar date dayKey() does — both are
// "whatever date these local digits say", never a UTC conversion. day here is
// built via local Date arithmetic (startOfDay/addDays), so toISOString() would
// shift it backward a day for anyone in a positive UTC offset (e.g. IST):
// local midnight Oct 19 is already Oct 18 18:30 UTC.
function localDateKey(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Card-style event row shared by the day/week marker-column layout and the
// agenda grouped-list layout. `degraded` is only set for events that are
// themselves a Zeph-created booking whose 'both' calendarChoice only
// succeeded on one provider leg — most calendar events aren't a Zeph
// booking at all, and those never get this banner.
const EventCard: React.FC<{ event: MergedEvent; degraded?: DegradedInfo }> = ({ event, degraded }) => {
  const { currentTheme: t } = useTheme()
  const attendee = attendeeLine(event)
  return (
    <div
      className="flex flex-col gap-2.5 rounded-xl p-3.5"
      style={{
        backgroundColor: t.sectionBackground,
        borderLeft: `3px solid ${t.accent}`,
        boxShadow: t.cardShadow,
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-bold text-white"
          style={{ backgroundColor: t.accent }}
        >
          {initialFor(event)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold truncate" style={{ color: t.text }}>{event.summary}</span>
            {event.status === 'tentative' && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded shrink-0"
                style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}
              >
                Tentative
              </span>
            )}
          </div>
          {attendee && (
            <div className="text-xs truncate mt-0.5" style={{ color: t.mutedText }}>{attendee}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0 ml-2">
          <span className="text-xs font-mono font-semibold" style={{ color: t.text }}>
            {event.isAllDay ? 'All day' : `${formatTime(event.start)} – ${formatTime(event.end)}`}
          </span>
          <span
            className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md"
            style={{ backgroundColor: t.hoverBackground, color: t.mutedText }}
            title={PROVIDER_LABEL[event.provider]}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: PROVIDER_DOT_COLOR[event.provider] }} />
            {PROVIDER_SHORT_LABEL[event.provider]}
          </span>
        </div>
      </div>
      {degraded && (
        <div
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5"
          style={{ backgroundColor: t.warningBackground, color: t.warningText }}
        >
          <TriangleAlert size={13} className="shrink-0" style={{ color: t.warningText }} />
          <span className="text-[11px] font-semibold">
            Only booked on {PROVIDER_SHORT_LABEL[degraded.succeededProvider]} — {PROVIDER_SHORT_LABEL[degraded.failedProvider]} failed
          </span>
        </div>
      )}
    </div>
  )
}

const CalendarView: React.FC<Props> = ({ workspaceId, refreshToken, bookButton }) => {
  const { currentTheme: t } = useTheme()
  const [view, setView] = useState<ViewMode>('week')
  const [referenceDate, setReferenceDate] = useState(() => startOfDay(new Date()))
  const [events, setEvents] = useState<MergedEvent[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<'needs_reconnect' | 'not_found' | 'generic' | null>(null)
  const [reconnectProviders, setReconnectProviders] = useState<CalendarProvider[]>([])
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([])
  const [accountFilter, setAccountFilter] = useState<CalendarProvider | null>(null)
  const [degradedMap, setDegradedMap] = useState<Map<string, DegradedInfo>>(new Map())

  const range = useMemo(() => {
    if (view === 'day') {
      const from = startOfDay(referenceDate)
      return { from, to: addDays(from, 1) }
    }
    if (view === 'week') {
      const from = startOfWeek(referenceDate)
      return { from, to: addDays(from, 7) }
    }
    // agenda — next 14 days from the reference date
    const from = startOfDay(referenceDate)
    return { from, to: addDays(from, 14) }
  }, [view, referenceDate])

  const fetchEvents = useCallback(async () => {
    setLoading(true)
    setError(null)
    setReconnectProviders([])
    const from = range.from.toISOString()
    const to = range.to.toISOString()

    const [google, outlook, bookings] = await Promise.all([
      fetchProviderEvents('google', getCalendarEvents, workspaceId, from, to),
      fetchProviderEvents('outlook', getOutlookCalendarEvents, workspaceId, from, to),
      // Degraded-booking correlation is an enhancement on top of the real
      // calendar data, not load-bearing for it — a failure here must never
      // block the calendar from rendering, so it's swallowed to [].
      listBookings(workspaceId, from, to).catch(() => []),
    ])

    const needsReconnect = [google, outlook].filter((r) => r.error === 'needs_reconnect').map((r) => r.provider)
    if (google.error === 'generic' || outlook.error === 'generic') {
      setEvents(null)
      setError('generic')
    } else if (needsReconnect.length > 0) {
      setEvents(null)
      setReconnectProviders(needsReconnect)
      setError('needs_reconnect')
    } else if (google.error === 'not_found' && outlook.error === 'not_found') {
      setEvents(null)
      setError('not_found')
    } else {
      setEvents([...google.events, ...outlook.events])
    }

    const nextDegraded = new Map<string, DegradedInfo>()
    for (const booking of bookings) {
      if (!booking.degraded) continue
      const ref = booking.calendarRefs[0]
      if (!ref) continue
      nextDegraded.set(`${ref.provider}:${ref.eventId}`, {
        succeededProvider: ref.provider,
        failedProvider: OTHER_PROVIDER[ref.provider],
      })
    }
    setDegradedMap(nextDegraded)

    setLoading(false)
  }, [workspaceId, range])

  useEffect(() => { fetchEvents() }, [fetchEvents, refreshToken])

  // Account filter options — at most one Google + one Outlook account can be
  // connected per rep (unique workspaceId+userId index on both repositories),
  // so this is a provider filter shown with the real connected email, not a
  // multi-account-per-provider picker.
  useEffect(() => {
    getIntegrations(workspaceId)
      .then((integrations) => {
        const next: ConnectedAccount[] = []
        for (const integration of integrations) {
          if (integration.status !== 'active' || !integration.identifier) continue
          if (integration.provider === 'google_calendar') next.push({ provider: 'google', email: integration.identifier })
          else if (integration.provider === 'outlook_calendar') next.push({ provider: 'outlook', email: integration.identifier })
        }
        setAccounts(next)
      })
      .catch(() => setAccounts([]))
  }, [workspaceId, refreshToken])

  const visibleEvents = useMemo(() => {
    if (!events) return events
    return accountFilter ? events.filter((e) => e.provider === accountFilter) : events
  }, [events, accountFilter])

  const grouped = useMemo(() => {
    const byDay = new Map<string, MergedEvent[]>()
    for (const event of visibleEvents ?? []) {
      const key = dayKey(event.start)
      const list = byDay.get(key) ?? []
      list.push(event)
      byDay.set(key, list)
    }
    for (const list of byDay.values()) {
      list.sort((a, b) => a.start.localeCompare(b.start))
    }
    return byDay
  }, [visibleEvents])

  // Summary counts reflect everything currently loaded for the visible range
  // (unfiltered) — the account filter only changes what's rendered below, not
  // what's been fetched.
  const summary = useMemo(() => {
    const list = events ?? []
    const tentativeCount = list.filter((e) => e.status === 'tentative').length
    const allDayCount = list.filter((e) => e.isAllDay).length
    const degradedCount = list.filter((e) => degradedMap.has(`${e.provider}:${e.id}`)).length
    const clauses: string[] = []
    if (tentativeCount > 0) clauses.push(`${tentativeCount} tentative`)
    if (allDayCount > 0) clauses.push(`${allDayCount} all-day`)
    if (degradedCount > 0) clauses.push(`${degradedCount} needs a second look`)
    const rangeLabel = view === 'day' ? 'today' : view === 'week' ? 'this week' : 'in the next 2 weeks'
    return {
      count: list.length,
      title: `${list.length} meeting${list.length === 1 ? '' : 's'} ${rangeLabel}`,
      subtitle: clauses.length > 0 ? clauses.join(' · ') : null,
    }
  }, [events, view, degradedMap])

  const daysInView = useMemo(() => {
    const count = view === 'day' ? 1 : view === 'week' ? 7 : 14
    return Array.from({ length: count }, (_, i) => addDays(range.from, i))
  }, [view, range])

  const navigate = (direction: -1 | 1) => {
    const step = view === 'day' ? 1 : view === 'week' ? 7 : 14
    setReferenceDate((prev) => addDays(prev, step * direction))
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Summary row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[18px] font-bold leading-tight" style={{ color: t.text }}>{summary.title}</p>
          {summary.subtitle && (
            <p className="text-[13px] mt-0.5" style={{ color: t.mutedText }}>{summary.subtitle}</p>
          )}
        </div>
        {bookButton}
      </div>

      {/* Toolbar row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: t.hoverBackground }}>
            {(['day', 'week', 'agenda'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setView(mode)}
                className="text-xs font-medium px-3 py-1.5 rounded-md capitalize transition-colors"
                style={{
                  backgroundColor: view === mode ? t.sectionBackground : 'transparent',
                  color: view === mode ? t.text : t.mutedText,
                  boxShadow: view === mode ? t.cardShadow : undefined,
                }}
              >
                {mode}
              </button>
            ))}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-[7px] rounded-[9px] transition-colors"
                style={{ border: `1px solid ${t.border}`, backgroundColor: t.sectionBackground, color: t.text }}
              >
                <Mail size={13} style={{ color: t.accent }} />
                <span className="max-w-[160px] truncate">
                  {accountFilter ? accounts.find((a) => a.provider === accountFilter)?.email ?? 'All accounts' : 'All accounts'}
                </span>
                <ChevronDown size={13} style={{ color: t.mutedText }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}`, boxShadow: t.cardShadow }}
            >
              <DropdownMenuItem
                onClick={() => setAccountFilter(null)}
                style={{ color: t.text, backgroundColor: accountFilter === null ? t.hoverBackground : 'transparent' }}
              >
                All accounts
              </DropdownMenuItem>
              {accounts.map((acc) => (
                <DropdownMenuItem
                  key={acc.provider}
                  onClick={() => setAccountFilter(acc.provider)}
                  style={{ color: t.text, backgroundColor: accountFilter === acc.provider ? t.hoverBackground : 'transparent' }}
                >
                  <div className="flex flex-col">
                    <span>{acc.email}</span>
                    <span className="text-xs" style={{ color: t.mutedText }}>{PROVIDER_LABEL[acc.provider]}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            aria-label="Previous"
            className="w-8 h-8 rounded-md flex items-center justify-center transition-colors"
            style={{ border: `1px solid ${t.border}`, color: t.text }}
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => setReferenceDate(startOfDay(new Date()))}
            className="text-xs font-medium px-3 py-1.5 rounded-md"
            style={{ border: `1px solid ${t.border}`, color: t.text }}
          >
            Today
          </button>
          <button
            onClick={() => navigate(1)}
            aria-label="Next"
            className="w-8 h-8 rounded-md flex items-center justify-center transition-colors"
            style={{ border: `1px solid ${t.border}`, color: t.text }}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 size={20} className="animate-spin" style={{ color: t.mutedText }} />
        </div>
      ) : error === 'needs_reconnect' ? (
        <div
          className="flex flex-col items-center gap-3 text-center py-16 rounded-xl"
          style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
        >
          <AlertTriangle size={24} style={{ color: t.errorText }} />
          <p className="text-sm font-medium" style={{ color: t.text }}>
            {reconnectProviders.map((p) => PROVIDER_LABEL[p]).join(' and ')} need{reconnectProviders.length === 1 ? 's' : ''} to be reconnected
          </p>
          <p className="text-xs max-w-sm" style={{ color: t.mutedText }}>
            Access expired for this calendar. Reconnect it from Settings → Integrations to see your schedule again.
          </p>
          <Link
            to="/dashboard/settings?tab=integrations"
            className="text-xs font-medium px-3 py-1.5 rounded-md text-white mt-1"
            style={{ backgroundColor: t.accent }}
          >
            Go to Integrations
          </Link>
        </div>
      ) : error === 'not_found' ? (
        <div
          className="flex flex-col items-center gap-3 text-center py-16 rounded-xl"
          style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
        >
          <CalendarX2 size={24} style={{ color: t.mutedText }} />
          <p className="text-sm font-medium" style={{ color: t.text }}>No calendar connected</p>
          <p className="text-xs max-w-sm" style={{ color: t.mutedText }}>
            Connect Google Workspace or Outlook Workspace from Settings → Integrations to see your schedule here.
          </p>
          <Link
            to="/dashboard/settings?tab=integrations"
            className="text-xs font-medium px-3 py-1.5 rounded-md text-white mt-1"
            style={{ backgroundColor: t.accent }}
          >
            Go to Integrations
          </Link>
        </div>
      ) : error === 'generic' ? (
        <div
          className="flex flex-col items-center gap-3 text-center py-16 rounded-xl"
          style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
        >
          <AlertTriangle size={24} style={{ color: t.errorText }} />
          <p className="text-sm font-medium" style={{ color: t.text }}>Couldn't load your calendar</p>
          <button
            onClick={fetchEvents}
            className="text-xs font-medium px-3 py-1.5 rounded-md mt-1"
            style={{ border: `1px solid ${t.border}`, color: t.text }}
          >
            Try again
          </button>
        </div>
      ) : !events || events.length === 0 ? (
        <div
          className="flex flex-col items-center gap-2 text-center py-16 rounded-xl"
          style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
        >
          <CalendarX2 size={24} style={{ color: t.mutedText }} />
          <p className="text-sm font-medium" style={{ color: t.text }}>No events in this range</p>
          <p className="text-xs" style={{ color: t.mutedText }}>Your calendar is clear.</p>
        </div>
      ) : visibleEvents && visibleEvents.length === 0 ? (
        <div
          className="flex flex-col items-center gap-2 text-center py-16 rounded-xl"
          style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
        >
          <CalendarX2 size={24} style={{ color: t.mutedText }} />
          <p className="text-sm font-medium" style={{ color: t.text }}>No events for this account</p>
          <p className="text-xs" style={{ color: t.mutedText }}>Try "All accounts" to see everything in this range.</p>
        </div>
      ) : view === 'agenda' ? (
        // Agenda view keeps a simpler grouped-list layout (day header bar
        // above its events) rather than the side-by-side marker column — at
        // up to 14 days of dense listing, a 52px marker column per day would
        // add width without adding clarity the header bar doesn't already give.
        <div className="flex flex-col gap-3">
          {daysInView.map((day) => {
            const key = localDateKey(day)
            const dayEvents = grouped.get(key) ?? []
            if (dayEvents.length === 0) return null
            return (
              <div key={key} className="flex flex-col gap-2">
                <div className="text-xs font-semibold" style={{ color: t.mutedText }}>
                  {formatDayHeader(day)}
                </div>
                <div className="flex flex-col gap-2">
                  {dayEvents.map((event) => (
                    <EventCard key={`${event.provider}-${event.id}`} event={event} degraded={degradedMap.get(`${event.provider}:${event.id}`)} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        // Day/week views: a left-side marker column per day (weekday + day
        // number) with that day's event cards in a column to its right.
        <div className="flex flex-col gap-4">
          {daysInView.map((day) => {
            const key = localDateKey(day)
            const dayEvents = grouped.get(key) ?? []
            return (
              <div key={key} className="flex items-start gap-4">
                <div className="w-[52px] shrink-0 flex flex-col items-center pt-1">
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: t.mutedText, letterSpacing: '0.08em' }}
                  >
                    {day.toLocaleDateString(undefined, { weekday: 'short' })}
                  </span>
                  <span className="text-[22px] font-bold" style={{ color: t.text }}>
                    {day.getDate()}
                  </span>
                </div>
                <div className="min-w-0 flex-1 flex flex-col gap-2">
                  {dayEvents.length === 0 ? (
                    <div
                      className="rounded-xl px-4 py-3.5 text-xs"
                      style={{ backgroundColor: t.sectionBackground, color: t.mutedText, border: `1px solid ${t.borderSecondary}` }}
                    >
                      No events
                    </div>
                  ) : (
                    dayEvents.map((event) => (
                      <EventCard key={`${event.provider}-${event.id}`} event={event} degraded={degradedMap.get(`${event.provider}:${event.id}`)} />
                    ))
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default CalendarView
